import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { CompanyDetails, CompanyPersonnelResponse, CompanySearchResponse } from '@models/registration.models';
import { environment } from '@environments/environment';
import { GenericPopupComponent } from '@components/shared/generic-popup.component';
import { GenericMessagePopupComponent, MessagePopupData } from '@components/shared/generic-message-popup.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { FuelType, Meter, MeterType, ShareInfo } from '@models/meter.model';
import { UserDetails } from './login.service';
import { LogService } from './log.service';

@Injectable({
  providedIn: 'root'
})
export class UtilService {
  private logService = inject(LogService);

  private baseUrlOrg = `${environment.baseUrlOrg}/org`;

  private _snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private http = inject(HttpClient);
  private breakpointObserver = inject(BreakpointObserver);
  
  private isMobileSubject = new BehaviorSubject<boolean>(false);
  isMobile$ = this.isMobileSubject.asObservable();

  constructor() {
    // Initialize breakpoint observer
    this.breakpointObserver.observe([
      Breakpoints.HandsetPortrait
    ]).subscribe(result => {
      this.isMobileSubject.next(result.matches);
    });
  }

  setItemToLocalStorage(key: string, value: string): void {
    localStorage.setItem(btoa(key), btoa(value));
  }

  getItemFromLocalStorage(key: string): string {
    return atob(localStorage.getItem(btoa(key)) || '');
  }

  removeItemFromLocalStorage(key: string): void {
    localStorage.removeItem(btoa(key));
  }

  openSnackBar(message: string, action: string, type: 'success' | 'error'): void {
    this._snackBar.open(message, action, {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: type === 'success' ? 'success-snackbar' : 'error-snackbar',
      duration: 5000
    });
    if (type === 'error') {
      this.logService.addErrorLog('ERROR_SNACKBAR', {message});
    }
  }

  showErrorMessageFromAPIResponse(message: string, error: any): void {
    const possibleError = error?.error?.errors?.[0] || error?.error?.message || error?.message || '';
    const errorMessage = `${message} ${possibleError ?  possibleError : 'Please try again.'}`;
    this.openSnackBar(errorMessage, 'Close', 'error');
  }

  openPopupCustom<T, D>(component: any, data?: D, width = '400px'): Observable<T> {
    const dialogRef = this.dialog.open(component, {
      maxWidth: width,
      width,
      data
    });
    return dialogRef.afterClosed();
  }

  openPopup(header: string, message: string, userActionType: 'YesOrNo' | 'Confirm' | 'None'): Observable<any> {
    const dialogRef = this.dialog.open(GenericPopupComponent, {
      width: '400px',
      data: {
        header,
        message,
        userActionType
      }
    });
    return dialogRef.afterClosed();
  }

  openCloseablePopup(header: string, message: string, disableClose = false): MatDialogRef<GenericPopupComponent> {
    const dialogRef = this.dialog.open(GenericPopupComponent, {
      width: '400px',
      disableClose,
      data: {
        header,
        message,
        userActionType: 'None' // No user action required, just a message
      }
    });
    return dialogRef;
  }

  /**
   * Show a generic message popup with customizable icon
   */
  showMessagePopup(header: string, message: string, icon?: string, iconColor?: string): Observable<any> {
    const data: MessagePopupData = {
      header,
      message,
      icon,
      iconColor
    };

    const dialogRef = this.dialog.open(GenericMessagePopupComponent, {
      width: '400px',
      data
    });
    return dialogRef.afterClosed();
  }

  /**
   * Show a success message popup with green tick icon
   */
  showSuccessMessage(header: string, message: string): Observable<any> {
    return this.showMessagePopup(header, message, 'check_circle', '#4caf50');
  }

  showErrorMessage(header: string, message: string): Observable<any> {
    this.logService.addErrorLog('ERROR_POPUP', {header, message});
    return this.showMessagePopup(header, message, 'error', '#f44336');
  }

  // API for company search
  searchCompanies(query: string): Observable<CompanyDetails[]> {
    const url = `${this.baseUrlOrg}/v1/search`;
    const params = {
      query
    };
    
    const headers = new HttpHeaders({
      'e_api_key': environment.apiKeyOrg
    });

    return this.http.get<CompanySearchResponse>(url, { 
      headers, 
      params 
    }).pipe(
      map(response => response.data)
    );
  }

  getCompanyDetailsFromLoginUser(user: UserDetails): Observable<CompanyDetails | null> {
    return of({
      name: user.companyName || '',
      number: user.companyId || '',
      type: user.companyType || '',
      companyStatus: 'ACTIVE'
    });
  }

  // // API for searching company by ID
  // searchCompanyById(companyNumber: string, companyType: string = 'ltd'): Observable<CompanyDetails | null> {
  //   const url = `${this.baseUrlCarbon}/v1/company-details`;
  //   const params = {
  //     type: companyType,
  //     number: companyNumber,
  //     utmSource: 'energyhelp'
  //   };
    
  //   const headers = new HttpHeaders({
  //     'e_api_key': environment.eApiKey
  //   });

  //   return this.http.get<CompanySearchResponse>(url, { 
  //     headers, 
  //     params 
  //   }).pipe(
  //     map(response => {
  //       if (response.data && response.data.length > 0) {
  //         const company = response.data[0];
  //         // Return only the fields that match our CompanyDetails model
  //         return {
  //           name: company.name,
  //           number: company.number,
  //           type: company.type,
  //           companyStatus: company.companyStatus
  //         };
  //       }
  //       return null;
  //     }),
  //     catchError(error => {
  //       console.error('Error searching company by ID:', error);
  //       return of(null);
  //     })
  //   );
  // }

  // API for fetching company personnel data
  getCompanyPersonnel(companyNumber: string, companyType: string): Observable<CompanyPersonnelResponse> {
    const url = `${this.baseUrlOrg}/v1/contact`;
    const params: any = {
      number: companyNumber,
      type: companyType
    };
    
    const headers = new HttpHeaders({
      'e_api_key': environment.apiKeyOrg
    });
    return this.http.get<CompanyPersonnelResponse>(url, { 
      headers, 
      params 
    });
  }

  getMeterBreakdown(meters: Meter[]): {mpan: string[], mprn: string[]} {
    const mpan: string[] = [];
    const mprn: string[] = [];

    meters.forEach(meter => {
      if (meter.fuelType === FuelType.ELEC) {
        mpan.push(meter.meterNumber);
      } else if (meter.fuelType === FuelType.GAS) {
        mprn.push(meter.meterNumber);
      }
    });

    return { mpan, mprn };
  }

  // API for validating email domain against company
  validateEmailDomainWithCompany(email: string, companyName: string): Observable<{isValid: boolean, message?: string}> {
    const url = `${environment.baseUrl}/core/v1/email-verification`;
    const requestBody = {
      email: email,
      companyName: companyName
    };

    return this.http.post<any>(url, requestBody).pipe(
      map(response => {
        if (response.status && response.code === 200 && response.data) {
          const isValid = response.data.status === 'valid';

          console.log('Email validation result:', response.data);
          
          if (isValid) {
            return { isValid: true };
          } else {
            return { 
              isValid: false, 
              message: `The email domain '${response.data.domain}' does not match the domain associated with the company. Please select the appropriate company name or continue anyway. The account will be validated manually.` 
            };
          }
        } else {
          // If response is not as expected, allow user to proceed
          return { 
            isValid: false, 
            message: `Could not verify email domain for '${companyName}'. Please ensure you're using a company email address or continue anyway.` 
          };
        }
      }),
      catchError(error => {
        console.error('Error validating email domain:', error);
        // On API error, allow user to proceed with warning
        return of({ 
          isValid: false, 
          message: `Unable to validate email domain at this time. Please ensure you're using a company email address or continue anyway.` 
        });
      })
    );
  }

  /**
   * Show confirmation dialog for revoking share access
   * @param share ShareInfo object containing share details
   * @param meterNumber Meter number to revoke access for
   * @returns Observable<boolean> - true if user confirmed, false if cancelled
   */
  confirmRevokeShare(share: ShareInfo, meterNumber: string): Observable<boolean> {
    const header = 'Confirm Revoke Access';
    const message = `Are you sure you want to revoke access for ${meterNumber} from ${share.shareeName}? This action cannot be undone.`;
    
    return this.openPopup(header, message, 'YesOrNo');
  }
}
